import { createSlice } from '@reduxjs/toolkit';
import { notifyUserError, notifyUserSuccess } from 'utils/notifications';

import type {
  OrganizationPresetListItem,
  OrganizationProgramItem,
  PresetTriggerFieldConfig,
  PresetTriggerFieldOption,
  PresetTriggerValueTypeConfig
} from './types';

import {
  createOrganizationPreset,
  deleteOrganizationPreset,
  getOrganizationPrograms,
  getOrganizationPresetTriggerConfig,
  getOrganizationPresetTriggerFields,
  getOrganizationPresetsList,
  updateOrganizationPreset,
  putOrganizationPresetStatuses
} from './presetsActions';

interface PresetsState {
  list: {
    content: OrganizationPresetListItem[];
    page: number;
    size: number;
    totalNumber: number;
    loading: boolean;
  };
  triggerFieldOptions: {
    items: PresetTriggerFieldOption[];
    loading: boolean;
  };
  triggerConfig: {
    fields: Record<string, PresetTriggerFieldConfig>;
    valueTypes: Record<string, PresetTriggerValueTypeConfig>;
    loading: boolean;
  };
  programs: {
    items: OrganizationProgramItem[];
    loading: boolean;
  };
  savingStatusChanges: boolean;
}

const initialState: PresetsState = {
  list: {
    content: [],
    page: 0,
    size: 10,
    totalNumber: 0,
    loading: false
  },
  triggerFieldOptions: {
    items: [],
    loading: false
  },
  triggerConfig: {
    fields: {},
    valueTypes: {},
    loading: false
  },
  programs: {
    items: [],
    loading: false
  },
  savingStatusChanges: false
};

export const presetsSlice = createSlice({
  name: 'presetsSlice',
  initialState,
  reducers: {
    resetPresetsState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrganizationPresetsList.pending, (state) => {
        state.list.loading = true;
      })
      .addCase(getOrganizationPresetsList.fulfilled, (state, action) => {
        state.list.content = action.payload.content;
        state.list.page = action.payload.page;
        state.list.size = action.payload.size;
        state.list.totalNumber = action.payload.totalNumber;
        state.list.loading = false;
      })
      .addCase(getOrganizationPresetsList.rejected, (state, action) => {
        state.list.loading = false;

        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(getOrganizationPresetTriggerFields.pending, (state) => {
        state.triggerFieldOptions.loading = true;
      })
      .addCase(getOrganizationPresetTriggerFields.fulfilled, (state, action) => {
        state.triggerFieldOptions.items = action.payload;
        state.triggerFieldOptions.loading = false;
      })
      .addCase(getOrganizationPresetTriggerFields.rejected, (state, action) => {
        state.triggerFieldOptions.loading = false;

        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(getOrganizationPresetTriggerConfig.pending, (state) => {
        state.triggerConfig.loading = true;
      })
      .addCase(getOrganizationPresetTriggerConfig.fulfilled, (state, action) => {
        state.triggerConfig.fields = action.payload.fields;
        state.triggerConfig.valueTypes = action.payload.valueTypes;
        state.triggerConfig.loading = false;
      })
      .addCase(getOrganizationPresetTriggerConfig.rejected, (state, action) => {
        state.triggerConfig.loading = false;

        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(getOrganizationPrograms.pending, (state) => {
        state.programs.loading = true;
      })
      .addCase(getOrganizationPrograms.fulfilled, (state, action) => {
        state.programs.items = action.payload;
        state.programs.loading = false;
      })
      .addCase(getOrganizationPrograms.rejected, (state, action) => {
        state.programs.loading = false;

        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(createOrganizationPreset.fulfilled, () => {
        notifyUserSuccess('Preset created successfully.');
      })
      .addCase(createOrganizationPreset.rejected, (_, action) => {
        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(updateOrganizationPreset.fulfilled, () => {
        notifyUserSuccess('Preset updated successfully.');
      })
      .addCase(updateOrganizationPreset.rejected, (_, action) => {
        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(putOrganizationPresetStatuses.pending, (state) => {
        state.savingStatusChanges = true;
      })
      .addCase(putOrganizationPresetStatuses.fulfilled, (state, action) => {
        state.list.content = state.list.content.map((preset) => {
          const updatedStatus = action.payload.statusesByPresetId[String(preset.id)];

          if (!updatedStatus) {
            return preset;
          }

          return {
            ...preset,
            status: updatedStatus
          };
        });

        state.savingStatusChanges = false;
        notifyUserSuccess('Your changes were saved.');
      })
      .addCase(putOrganizationPresetStatuses.rejected, (state, action) => {
        state.savingStatusChanges = false;

        if (action.payload) {
          notifyUserError(action.payload);
        }
      })
      .addCase(deleteOrganizationPreset.fulfilled, (state, action) => {
        state.list.content = state.list.content.filter(
          (preset) => preset.id !== action.payload.deletedPresetId
        );
        state.list.totalNumber = Math.max(0, state.list.totalNumber - 1);
        notifyUserSuccess('Preset deleted successfully.');
      })
      .addCase(deleteOrganizationPreset.rejected, (_, action) => {
        if (action.payload) {
          notifyUserError(action.payload);
        }
      });
  }
});

export const { resetPresetsState } = presetsSlice.actions;

export default presetsSlice.reducer;
